"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { POPULAR_EMAIL_DOMAINS } from "@/data/popular-email-domains";

interface EmailAutocompleteProps extends Omit<React.ComponentPropsWithoutRef<"input">, "onChange" | "onBlur"> {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: boolean;
}

const EmailAutocomplete = React.forwardRef<HTMLInputElement, EmailAutocompleteProps>(
  ({ value, onChange, onBlur, placeholder, className, error, ...props }, ref) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [filteredDomains, setFilteredDomains] = useState<string[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Determine if this is a controlled input (value prop provided) or uncontrolled
    const isControlled = value !== undefined;

    // Merge refs: support both React Hook Form ref and internal ref
    const setRefs = useCallback((node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    }, [ref]);

    // Extract domain part after "@" symbol
    const getDomainPart = (email: string): string => {
      if (!email) return "";
      const atIndex = email.lastIndexOf("@");
      if (atIndex === -1) return "";
      return email.substring(atIndex + 1).toLowerCase();
    };

    // Get current email value (from prop if controlled, from input element if uncontrolled)
    const getCurrentValue = useCallback(() => {
      if (isControlled) {
        return value || "";
      }
      return inputRef.current?.value || "";
    }, [isControlled, value]);

    // Check and update suggestions based on current input value
    const updateSuggestions = useCallback(() => {
      const emailValue = getCurrentValue();
      
      // Show suggestions if "@" is present
      if (emailValue.includes("@")) {
        const domainPart = getDomainPart(emailValue);
        if (domainPart.length > 0) {
          // Filter based on what's typed after "@"
          const filtered = POPULAR_EMAIL_DOMAINS.filter((domain) =>
            domain.toLowerCase().startsWith(domainPart)
          );
          setFilteredDomains(filtered.length > 0 ? filtered : [...POPULAR_EMAIL_DOMAINS]);
        } else {
          // Show only top 6 most popular domains if "@" is typed but nothing after it
          const topDomains = POPULAR_EMAIL_DOMAINS.slice(0, 6);
          setFilteredDomains(topDomains);
        }
        setShowSuggestions(true);
        setSelectedIndex(-1);
      } else {
        setShowSuggestions(false);
        setFilteredDomains([]);
      }
    }, [getCurrentValue]);

    // Filter domains based on typed text - use a timer to check input value for uncontrolled inputs
    useEffect(() => {
      if (isControlled) {
        updateSuggestions();
      }
    }, [value, isControlled, updateSuggestions]);

    // Handle input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Always call React Hook Form's onChange if provided
      onChange?.(e);
      
      // Trigger suggestion check immediately
      updateSuggestions();
    };

    // Also handle input event for better real-time updates (especially for uncontrolled inputs)
    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
      if (!isControlled) {
        updateSuggestions();
      }
    };

    // Handle suggestion selection
    const handleSuggestionClick = (domain: string) => {
      const emailValue = getCurrentValue();
      const atIndex = emailValue.lastIndexOf("@");
      if (atIndex !== -1) {
        const localPart = emailValue.substring(0, atIndex + 1);
        const newValue = localPart + domain;
        
        // For uncontrolled inputs, set the value directly on the input element
        if (!isControlled && inputRef.current) {
          inputRef.current.value = newValue;
          // Trigger input event for React Hook Form
          const event = new Event("input", { bubbles: true });
          inputRef.current.dispatchEvent(event);
        }
        
        // Create synthetic event that matches React Hook Form's expected format
        const syntheticEvent = {
          target: { value: newValue, name: props.name || "" },
          currentTarget: { value: newValue, name: props.name || "" },
        } as React.ChangeEvent<HTMLInputElement>;
        
        if (onChange) {
          onChange(syntheticEvent);
        }
      }
      setShowSuggestions(false);
      inputRef.current?.focus();
    };

    // Handle keyboard navigation (optional for chips, but keeping for accessibility)
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showSuggestions || filteredDomains.length === 0) {
        return;
      }

      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredDomains.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < filteredDomains.length) {
            handleSuggestionClick(filteredDomains[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setShowSuggestions(false);
          setSelectedIndex(-1);
          break;
      }
    };

    // Handle blur
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Delay to allow click events on suggestions to fire
      setTimeout(() => {
        if (!containerRef.current?.contains(document.activeElement)) {
          setShowSuggestions(false);
          setSelectedIndex(-1);
          // Call React Hook Form's onBlur if provided
          if (onBlur) {
            onBlur(e);
          }
        }
      }, 200);
    };

    // Close suggestions when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setShowSuggestions(false);
          setSelectedIndex(-1);
        }
      };

      if (showSuggestions) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [showSuggestions]);

    // Build input props - only include value if controlled
    const inputProps: React.ComponentPropsWithoutRef<"input"> = {
      type: "email",
      onChange: handleChange,
      onInput: handleInput,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      placeholder,
      className: error ? `border-red-500 ${className || ""}` : className,
      ...props,
    };

    // Only add value prop if this is a controlled input
    if (isControlled) {
      inputProps.value = value || "";
    }

    return (
      <div ref={containerRef} className="w-full">
        <Input
          ref={setRefs}
          {...inputProps}
        />
        {showSuggestions && filteredDomains.length > 0 && (
          <div
            ref={suggestionsRef}
            className="mt-2 flex flex-wrap gap-2"
          >
            {filteredDomains.map((domain, index) => {
              const emailValue = getCurrentValue();
              const atIndex = emailValue.lastIndexOf("@");
              const localPart = atIndex !== -1 ? emailValue.substring(0, atIndex + 1) : "";
              const fullEmail = localPart + domain;
              
              return (
                <button
                  key={domain}
                  type="button"
                  className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                    index === selectedIndex
                      ? "bg-gray-100 border-gray-300 text-gray-900"
                      : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                  }`}
                  onClick={() => handleSuggestionClick(domain)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {fullEmail}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

EmailAutocomplete.displayName = "EmailAutocomplete";

export default EmailAutocomplete;

